import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosInstance } from '../../../../Common/Interceptors/AxiosConfig';

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const followWorkspace = createAsyncThunk(
  'social/followWorkspace',
  async (workspaceId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/workspaces/${workspaceId}/follow`);
      return { workspaceId, ...response.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to follow');
    }
  }
);

export const unfollowWorkspace = createAsyncThunk(
  'social/unfollowWorkspace',
  async (workspaceId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/workspaces/${workspaceId}/unfollow`);
      return { workspaceId, ...response.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to unfollow');
    }
  }
);

export const fetchFollowStatus = createAsyncThunk(
  'social/fetchFollowStatus',
  async (workspaceId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/workspaces/${workspaceId}/follow-status`);
      return { workspaceId, following: response.data.following };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch status');
    }
  },
  {
    condition: (workspaceId, { getState }) => {
      const { social } = getState();
      // If it's already in the map (even as false from a pending state), don't fetch again
      if (social.followingMap && social.followingMap[workspaceId] !== undefined) {
        return false;
      }
    }
  }
);

export const fetchFollowingList = createAsyncThunk(
  'social/fetchFollowingList',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/workspaces/me/following');
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch following');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const socialSlice = createSlice({
  name: 'social',
  initialState: {
    followingMap: {}, // { workspaceId: boolean }
    followingList: [],
    loading: false,
    error: null
  },
  reducers: {
    clearSocialError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Follow Status
      .addCase(fetchFollowStatus.pending, (state, action) => {
        // Prevent infinite loops by setting it to false temporarily while fetching
        if (state.followingMap[action.meta.arg] === undefined) {
           state.followingMap[action.meta.arg] = false; 
        }
      })
      .addCase(fetchFollowStatus.fulfilled, (state, action) => {
        state.followingMap[action.payload.workspaceId] = action.payload.following;
      })
      .addCase(fetchFollowStatus.rejected, (state, action) => {
        // Prevent infinite loops if the request fails
        state.followingMap[action.meta.arg] = false;
      })
      // Follow
      .addCase(followWorkspace.pending, (state) => {
        state.loading = true;
      })
      .addCase(followWorkspace.fulfilled, (state, action) => {
        state.loading = false;
        state.followingMap[action.payload.workspaceId] = true;
      })
      .addCase(followWorkspace.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Unfollow
      .addCase(unfollowWorkspace.fulfilled, (state, action) => {
        state.followingMap[action.payload.workspaceId] = false;
      })
      // Following List
      .addCase(fetchFollowingList.fulfilled, (state, action) => {
        state.followingList = action.payload;
        // Seed the map
        action.payload.forEach(ws => {
          state.followingMap[ws._id] = true;
        });
      });
  }
});

export const { clearSocialError } = socialSlice.actions;
export default socialSlice.reducer;
